import { CSSProperties, forwardRef, ReactNode, Ref } from 'react';
import Card, { CardProps } from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Divider from '@mui/material/Divider';
import { SxProps, Theme } from '@mui/material/styles';

interface MainCardProps extends Omit<CardProps, 'title' | 'content'> {
  border?: boolean;
  boxShadow?: boolean;
  children?: ReactNode;
  subheader?: ReactNode;
  content?: boolean;
  contentSX?: SxProps<Theme>;
  darkTitle?: boolean;
  divider?: boolean;
  elevation?: number;
  secondary?: ReactNode;
  shadow?: string;
  sx?: SxProps<Theme>;
  title?: ReactNode;
  codeHighlight?: boolean;
  codeString?: string;
  modal?: boolean;
  ref?: Ref<HTMLDivElement>;
}

const MainCard = forwardRef<HTMLDivElement, MainCardProps>(function MainCard(
  {
    border = true,
    boxShadow,
    children,
    subheader,
    content = true,
    contentSX = {},
    darkTitle,
    divider = true,
    elevation,
    secondary,
    shadow,
    sx = {},
    title,
    codeHighlight = false,
    codeString,
    modal = false,
    ...others
  },
  ref
) {
  void codeString;
  return (
    <Card
      elevation={elevation || 0}
      sx={(theme: Theme) => ({
        position: 'relative' as CSSProperties['position'],
        ...(border && { border: `1px solid ${theme.palette.grey.A800 ?? theme.palette.grey[300]}` }),
        borderRadius: 1.5,
        // Premium: cards ganham sombra suave também no claro (antes ficavam chapados, só com borda).
        boxShadow: shadow || theme.customShadows.z1,
        transition: 'box-shadow 0.2s ease',
        ':hover': { boxShadow: shadow || theme.customShadows.z1 },
        // No dark, a borda clara vira uma "moldura" dura — usa borda sutil + leve sombra (padrão Mantis).
        ...theme.applyStyles('dark', {
          borderColor: 'rgba(255, 255, 255, 0.12)',
          boxShadow: theme.customShadows.z1,
          ':hover': { boxShadow: theme.customShadows.z1 }
        }),
        ...(codeHighlight && {
          '& pre': { margin: 0, padding: '12px !important', fontFamily: theme.typography.fontFamily, fontSize: '0.75rem' }
        }),
        ...(modal && {
          position: 'absolute' as CSSProperties['position'],
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: `calc(100% - 50px)`, sm: 'auto' },
          maxWidth: 768
        }),
        ...(typeof sx === 'function' ? (sx as (t: Theme) => object)(theme) : sx || {})
      })}
      ref={ref}
      {...others}
    >
      {!darkTitle && title && (
        <CardHeader
          sx={{ p: 2.5 }}
          slotProps={{
            title: { variant: darkTitle ? 'h4' : 'subtitle1' },
            action: { sx: { m: '0px auto', alignSelf: 'center' } }
          }}
          title={title}
          action={secondary}
          subheader={subheader}
        />
      )}

      {title && divider && <Divider />}

      {content && <CardContent sx={contentSX}>{children}</CardContent>}
      {!content && children}
    </Card>
  );
});

export default MainCard;
